import { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useChainProvider } from "../../lib/rpc/useChainProvider";
import { detectBundle } from "../../lib/forensics/bundle";
import { clusterByFunding, linkedWalletCount } from "../../lib/forensics/cluster";
import { assessRisk, type RiskTier } from "../../lib/forensics/risk";
import { runDossierViaBridge } from "../../lib/ai/bridge";
import { feedPausedAtom } from "../../state/atoms";
import type { DossierEvidence, Launch } from "../../lib/schemas";

const riskColor: Record<RiskTier, string> = {
  UNAVAILABLE: "var(--flurry-mid)",
  CRITICAL: "var(--flurry-red)",
  HIGH: "var(--flurry-amber)",
  MODERATE: "var(--flurry-cyan)",
  LOW: "var(--flurry-green)",
};

const short = (a: string) => `${a.slice(0, 8)}...${a.slice(-4)}`;
const chainLabel = (chain: Launch["chain"]) => (chain === "avalanche" ? "AVAX" : "RHC");
const rowKey = (row: Pick<Launch, "mint" | "poolAddress" | "proofTxHash">) =>
  `${row.proofTxHash ?? "no-proof"}:${row.poolAddress ?? "no-pool"}:${row.mint}`;

interface Row extends Launch {
  open: boolean;
  dossier: string | null;
  dossierLoading: boolean;
  dossierError: string | null;
  forensicsLoading: boolean;
  forensicsLoaded: boolean;
}

export function Scanner() {
  const [rows, setRows] = useState<Row[]>([]);
  const [paused, setPaused] = useAtom(feedPausedAtom);
  const provider = useChainProvider();
  const pausedRef = useRef(paused);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    setRows([]);
    const unsub = provider.subscribeLaunches((l) => {
      setRows((rs) => {
        if (pausedRef.current) return rs;
        return [
          {
            ...l,
            open: false,
            dossier: null,
            dossierLoading: false,
            dossierError: null,
            forensicsLoading: false,
            forensicsLoaded: false,
          },
          ...rs,
        ].slice(0, 40);
      });
    });
    return unsub;
  }, [provider]);

  const patch = (target: Row, update: Partial<Row>) =>
    setRows((rows) =>
      rows.map((row) => (rowKey(row) === rowKey(target) ? { ...row, ...update } : row)),
    );

  const toggle = (r: Row) => {
    const opening = !r.open;
    patch(r, { open: opening });
    if (opening && !r.forensicsLoaded && !r.forensicsLoading && provider.loadForensics) {
      patch(r, { forensicsLoading: true });
      provider
        .loadForensics(r)
        .then((update) => patch(r, { ...update, forensicsLoading: false, forensicsLoaded: true }))
        .catch(() =>
          patch(r, {
            forensicsLoading: false,
            forensicsLoaded: true,
            forensicsStatus: "degraded",
          }),
        );
    }
  };

  const analyze = (r: Row) => {
    const bundle = detectBundle(r.deploySlot, r.slotActivity);
    const clusters = clusterByFunding(r.slotActivity);
    const linked = linkedWalletCount(clusters);
    const risk = assessRisk(r.riskEvidence);
    return { bundle, clusters, linked, risk };
  };

  const onDossier = async (r: Row) => {
    const { bundle, linked } = analyze(r);
    const evidence: DossierEvidence = {
      chain: r.chain,
      ticker: r.ticker,
      platformLabel: r.platformLabel,
      deployer: r.deployer,
      bundled: bundle.bundled,
      bundleWallets: bundle.deploySlotWallets,
      firstBlockSupplyPct: bundle.deploySlotSupplyPct,
      linkedWallets: linked,
      deployerPriorLaunches: r.deployerPriorLaunches,
      deployerPriorRugs: r.deployerPriorRugs,
      devHoldsPct: r.devHoldsPct,
      listingStatus: r.listingStatus,
      forensicsStatus: r.forensicsStatus,
      marketDataStatus: r.marketDataStatus,
    };
    patch(r, { dossierLoading: true, dossierError: null });
    try {
      const dossier = await runDossierViaBridge(evidence);
      patch(r, { dossier, dossierLoading: false });
    } catch {
      patch(r, {
        dossierLoading: false,
        dossierError: "Hosted analysis unavailable. Evidence remains visible; try again later.",
      });
    }
  };

  return (
    <div>
      <div
        className="mb-2 flex items-center justify-between text-xs"
        style={{ color: "var(--flurry-mid)" }}
      >
        <span>VERIFIED LISTINGS // click a row for evidence</span>
        <button
          onClick={() => setPaused((p) => !p)}
          className="px-3 py-1"
          style={{
            color: paused ? "var(--flurry-amber)" : "var(--flurry-mid)",
            border: "1px solid var(--flurry-dim)",
            cursor: "pointer",
          }}
        >
          {paused ? "▶ RESUME FEED" : "⏸ PAUSE FEED"}
        </button>
      </div>
      <div style={{ border: "1px solid var(--flurry-dim)" }}>
        <div
          className="scanner-grid grid px-2 py-1 text-xs"
          style={{
            gridTemplateColumns: "70px 90px 1fr 90px 60px 80px",
            color: "var(--flurry-mid)",
            borderBottom: "1px solid var(--flurry-dim)",
          }}
        >
          <span>AGE</span>
          <span data-mobile-hide>PLATFORM</span>
          <span>TOKEN</span>
          <span data-mobile-hide>MCAP</span>
          <span data-mobile-hide>LINKS</span>
          <span>RISK</span>
        </div>
        {rows.map((r) => {
          const { bundle, clusters, linked, risk } = analyze(r);
          const ageSec = Math.floor((Date.now() - r.launchedAt) / 1000);
          const forensicsVerified = r.forensicsStatus === "verified";
          return (
            <div key={rowKey(r)} style={{ borderBottom: "1px solid var(--flurry-dim)" }}>
              <div
                onClick={() => toggle(r)}
                className="scanner-grid grid cursor-pointer px-2 py-1 text-sm hover:bg-white/5"
                style={{ gridTemplateColumns: "70px 90px 1fr 90px 60px 80px" }}
              >
                <span style={{ color: "var(--flurry-mid)" }}>
                  {ageSec < 60 ? `${ageSec}s` : `${Math.floor(ageSec / 60)}m`}
                </span>
                <span
                  data-mobile-hide
                  style={{
                    color: "var(--flurry-cyan)",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ color: "var(--flurry-mid)" }}>{chainLabel(r.chain)}·</span>
                  {r.platformLabel}
                </span>
                <span
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  ${r.ticker} <span style={{ color: "var(--flurry-mid)" }}>· {r.name}</span>
                </span>
                <span data-mobile-hide>
                  {r.marketDataStatus === "verified"
                    ? `$${Math.round(r.mcapUsd / 1000)}k`
                    : "unavailable"}
                </span>
                <span
                  data-mobile-hide
                  style={{
                    color:
                      forensicsVerified && linked > 5 ? "var(--flurry-amber)" : "var(--flurry-mid)",
                  }}
                >
                  {forensicsVerified ? linked : "—"}
                </span>
                <span
                  style={{
                    color: riskColor[risk.tier],
                    textShadow: `0 0 8px ${riskColor[risk.tier]}`,
                  }}
                  aria-label={`Risk ${risk.tier}, score ${risk.minScore} to ${risk.maxScore}, ${risk.coveragePct}% coverage`}
                >
                  {risk.tier === "UNAVAILABLE" ? `${risk.minScore}–${risk.maxScore}` : risk.tier}
                </span>
              </div>
              {r.open && (
                <div
                  className="px-3 pb-3 pt-1 text-xs"
                  style={{ background: "var(--flurry-panel)" }}
                >
                  <pre className="whitespace-pre-wrap">
                    {`${r.chain === "avalanche" ? "pool creator" : "deployer"}  ${short(r.deployer)}${forensicsVerified ? `   (${r.deployerPriorLaunches} prior launches, ${r.rugHistoryVerified ? `${r.deployerPriorRugs} rugs` : "rug history: unavailable"})` : "   (history unavailable)"}
token         ${short(r.mint)}
pool          ${r.poolAddress ? short(r.poolAddress) : "unavailable"}
listing proof ${r.listingStatus}${r.proofTxHash ? ` · ${short(r.proofTxHash)}` : ""}
bundle check  ${r.forensicsLoading ? "loading..." : forensicsVerified ? (bundle.bundled ? `BUNDLED — ${bundle.deploySlotWallets} wallets bought in deploy block` : "no bundle signal detected") : r.forensicsStatus}
first block   ${forensicsVerified ? `${bundle.deploySlotSupplyPct}% of supply acquired${bundle.deploySlotSupplyPct > 30 ? "  ⚠ concentration" : ""}` : "unavailable"}
wallet links  ${forensicsVerified ? `${linked} wallets share funding lineage${clusters[0] ? `\nfunder        ${short(clusters[0].funder)} (${clusters[0].wallets.length}-wallet cluster)` : ""}` : "unavailable"}
dev holds     ${forensicsVerified ? `${r.devHoldsPct}% of supply` : "unavailable"}`}
                  </pre>
                  <div
                    className="mt-2 grid gap-2 p-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
                    style={{ border: "1px solid var(--flurry-dim)" }}
                    aria-label={`Risk evidence for ${r.ticker}`}
                  >
                    <div>
                      <div style={{ color: riskColor[risk.tier] }}>
                        RISK {risk.tier} // {risk.minScore}–{risk.maxScore}/100
                      </div>
                      <div style={{ color: "var(--flurry-mid)" }}>
                        COVERAGE {risk.coveragePct}% // CONFIDENCE {risk.confidence}
                      </div>
                      <div className="mt-2" style={{ color: "var(--flurry-green)" }}>
                        REASONS
                      </div>
                      {risk.reasons.length ? (
                        <ul className="list-none">
                          {risk.reasons.map((reason) => (
                            <li key={reason}>+ {reason}</li>
                          ))}
                        </ul>
                      ) : (
                        <div style={{ color: "var(--flurry-mid)" }}>— no verified risk trigger</div>
                      )}
                    </div>
                    <div>
                      <div style={{ color: "var(--flurry-amber)" }}>EVIDENCE GAPS</div>
                      {risk.gaps.length ? (
                        <ul className="list-none" style={{ color: "var(--flurry-mid)" }}>
                          {risk.gaps.map((gap) => (
                            <li key={gap}>? {gap}</li>
                          ))}
                        </ul>
                      ) : (
                        <div style={{ color: "var(--flurry-green)" }}>✓ all signals verified</div>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      void onDossier(r);
                    }}
                    disabled={r.dossierLoading}
                    className="mt-2 px-3 py-1 text-xs"
                    style={{
                      color: "var(--flurry-bg)",
                      background: "var(--flurry-cyan)",
                      border: "none",
                      cursor: r.dossierLoading ? "wait" : "pointer",
                      opacity: r.dossierLoading ? 0.5 : 1,
                    }}
                  >
                    {r.dossierLoading
                      ? "ANALYZING..."
                      : r.dossier
                        ? "RERUN HOSTED DOSSIER"
                        : "▸ RUN HOSTED DOSSIER"}
                  </button>
                  {r.dossierError && (
                    <div className="mt-2" role="alert" style={{ color: "var(--flurry-red)" }}>
                      {r.dossierError}
                    </div>
                  )}
                  {r.dossier && (
                    <pre
                      className="mt-2 whitespace-pre-wrap p-2"
                      style={{
                        color: "var(--flurry-cyan)",
                        border: "1px dashed var(--flurry-dim)",
                      }}
                    >
                      {r.dossier}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
